import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { ConfirmHost } from '../components/Confirm';
import { Hud } from '../components/Hud';
import { AgendaPage } from '../features/agenda/AgendaPage';
import { BoardPage } from '../features/board/BoardPage';
import { NotesPage } from '../features/notes/NotesPage';
import { useMapState } from '../features/orgmap/mapState';
import { RoutinesPage } from '../features/routines/RoutinesPage';
import { CommandPalette, usePalette } from '../features/search/CommandPalette';
import { takeTaskLink } from '../lib/deepLink';
import { openSheet } from '../state/sheets';
import { useUI } from '../state/ui';
import { SheetHost } from './SheetHost';
import { Sidebar } from './Sidebar';

// O mapa traz o ELK (~1,5 MB): só carrega quando a seção é aberta.
const OrgMapPage = lazy(() => import('../features/orgmap/OrgMapPage').then(m => ({ default: m.OrgMapPage })));

// O editor de processos traz o React Flow e o ELK: também só quando abre.
const ProcessesPage = lazy(() => import('../features/processes/ProcessesPage').then(m => ({ default: m.ProcessesPage })));

const SECTION_TINT: Record<string, string> = { agenda: 'agenda', notas: 'notas' };

export function Shell() {
  const path = useLocation().pathname;
  const section = path.split('/')[1] || 'quadro';
  const { sidebarHidden, sidebarOpen } = useUI();
  const fullscreen = useMapState(s => s.fullscreen) && section === 'mapa';
  // Link direto para uma tarefa (cartões do Teams): abre a ficha por cima do quadro.
  useEffect(() => { const id = takeTaskLink(); if (id) openSheet({ kind: 'task', id }); }, []);
  // Busca geral: Ctrl+K (⌘K no Mac) em qualquer tela, inclusive com um campo em foco.
  const palette = usePalette(s => s.open);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        usePalette.setState(s => ({ open: !s.open }));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const cls = ['app', sidebarHidden && 'sem-sidebar', sidebarOpen && 'sidebar-aberta', fullscreen && 'mapa-cheia'].filter(Boolean).join(' ');
  return (
    <>
      <div className={cls} data-secao={SECTION_TINT[section] ?? section}>
        <Sidebar />
        <div className="main">
          <Routes>
            <Route path="/" element={<BoardPage />} />
            <Route path="/rotinas" element={<RoutinesPage />} />
            <Route path="/mapa" element={<Suspense fallback={<div className="loading">Carregando o mapa…</div>}><OrgMapPage /></Suspense>} />
            <Route path="/processos" element={<Suspense fallback={<div className="loading">Carregando os processos…</div>}><ProcessesPage /></Suspense>} />
            <Route path="/processos/:id" element={<Suspense fallback={<div className="loading">Carregando o processo…</div>}><ProcessesPage /></Suspense>} />
            <Route path="/agenda" element={<AgendaPage />} />
            <Route path="/notas" element={<NotesPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
      <SheetHost />
      {palette && <CommandPalette onClose={() => usePalette.setState({ open: false })} />}
      <ConfirmHost />
      <Hud />
    </>
  );
}
